import { Component, OnInit, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Utils } from '../utils/utils';
import { DaySlotsHash } from '../models/schedule/day-slots-hash.interface';
import { PopoverService } from '../popover/popover.service';
import * as moment from 'moment-timezone';

declare var $:any;

@Component({
    selector: 'app-calendar',
    templateUrl: 'calendar.component.html',
    standalone: false
})
export class CalendarComponent implements OnInit, OnChanges {

    @Input() startDate: Date = new Date();
    @Input() highlightDate: Date;
    @Input() schedule: DaySlotsHash;
    @Input() availableAppts: number;

    @Output() dateSelected: EventEmitter<Date> = new EventEmitter<Date>();

    public weeks: Array<any>; // the calendar
    public month: string; // month displayed at the top of the calendar
    public year: string; // the year displayed at the top of the calendar
    private minDate = new Date(); // today is the minimum selectable date
    private displayDate: Date; // the date used to make the calendar
    public moment: any; //to get the time zone of the selected location

    constructor(private popoverService: PopoverService) {
        this.moment = moment;
    }

    ngOnInit() {
        this.setCalendar();
    }

    ngOnChanges(changes: SimpleChanges) {
        // one of the inputs changed lets update the calendar
        this.setCalendar();
    }

    /**
     * Determines if a date should be enabled (clickable on the calendar)
     * @param {number} day the day of the displayed month
     * @returns {boolean} true if the date >= today and the date has appointments available
     */
    public enableDate(day: number): boolean {
        if (day !== null) {
            let dateToCheck: Date = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), day);
            if (Utils.CompareDates(this.minDate, dateToCheck) < 0) {
                return false;
            } else {
                if (this.schedule) {
                    if (this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)]) {
                        // tslint:disable-next-line:max-line-length
                        if (this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)].totalRemainingAppointements > 0) {
                            // there are appointments left to shedule on this day lets enable it
                            return true;
                        }
                    }
                }
                return false;
            }
        } else {
            return false;
        }
    }

    /**
     * Dtermines if a date should be have the not available class applied
     * @param {number} day the date of the displayed month
     * @returns {boolean} true if the date >= today and the date has >= number of filter appointment number
     */
    public hasAvailableAppts(day: number, forDisplay: boolean = true): boolean {
        let dateToCheck: Date = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), day);
        if (Utils.CompareDates(this.minDate, dateToCheck) < 0) {
            return false;
        } else {
            if (this.schedule && this.availableAppts > 0) {
                if (this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)]) {
                    let numberAptsTocheck: number = 1;
                    if (forDisplay) {
                        numberAptsTocheck = this.availableAppts;
                    }
                    if (this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)].totalRemainingAppointements
                        >= numberAptsTocheck) {
                        // there are appointments left to shedule on this day lets enable it
                        return true;
                    }
                }
            }
            return false;
        }
    }

    /**
     * Determines if the current displayed date is the highlight date
     * @param {number} day the day of the displayed month
     * @returns {boolean} true if this day is the same as the highlight date
     */
    public isHighlighted(day: number): boolean {
        if (this.highlightDate) {
            if (this.highlightDate.getFullYear() === this.displayDate.getFullYear()) {
                if (this.highlightDate.getMonth() === this.displayDate.getMonth()) {
                    if (this.highlightDate.getDate() === day) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    /**
     * Determines if the current displayed date is today
     * @param {number} day the day of the displayed month
     * @returns {boolean} true if the day is the same as today
     */
    protected isToday(day: number): boolean {
        let today: Date = new Date();
        if (today.getFullYear() === this.displayDate.getFullYear()) {
            if (today.getMonth() === this.displayDate.getMonth()) {
                if (today.getDate() === day) {
                    return true;
                }
            }
        }
        return false;
    }

    public getNextAvailableTime(day: number): string {
        let nextTime = '';
        if (this.hasAvailableAppts(day, false)) {
            let selectedDate = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), day);
            let locationTimeZone = moment.tz(this.schedule[Utils.getShortISOYearMonthDay(selectedDate)].timezone).format('z');
            this.schedule[Utils.getShortISOYearMonthDay(selectedDate)].slots.some(slot => {
                if (slot.available > 0 && !slot.expired) {
                    nextTime = slot.slotDisplayHour + ':' + slot.slotDisplayMinute;
                    if (slot.slotHour >= 12) {
                        nextTime += ' PM';
                    } else {
                        nextTime += ' AM';
                    }
                    if (this.schedule[Utils.getShortISOYearMonthDay(selectedDate)].timezone !== '') {
                        nextTime += ' (' + this.schedule[Utils.getShortISOYearMonthDay(selectedDate)].timezone + ')'+'(' + locationTimeZone + ')';
                    }
                    return true;
                }
                return false;
            });
        }
        return nextTime;
    }

    public getDisplayDate(day: number): string {
        if (day !== null) {
            let year: string = this.displayDate.getFullYear().toString();
            let month: string;
            if (this.displayDate.getMonth() + 1 < 10) {
                month = '0' + (this.displayDate.getMonth() + 1);
            } else {
                month = (this.displayDate.getMonth() + 1).toString();
            }
            let date: string;
            if (day < 10) {
                date = '0' + day;
            } else {
                date = day.toString();
            }
            return month + '/' + date + '/' + year;
        } else {
            return '6/23/1995';
        }
    }

    public getRemainingSlotsForDay(day: number): number {
        if (this.hasAvailableAppts(day, false)) {
            let selectedDate = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), day);
            return this.schedule[Utils.getShortISOYearMonthDay(selectedDate)].totalRemainingAppointements;
        }
        return 0;
    }

    public onDaySelected(day: number): number {
        if (this.enableDate(day)) {
            let selectedDate = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), day);
            this.dateSelected.emit(selectedDate);
        }
        return 0;
    }

    public focusElement(tagID: string) {
        $('#' + tagID).focus();
    }

    public showPopover(tagID: string) {
        this.popoverService.openPopover(tagID);
    }

    public hidePopovers() {
        this.popoverService.closeOtherPopovers(null);
    }

    /**
     * Dtermines if a date should be have the not available class applied
     * @param {number} day the date of the displayed month
     * @returns {boolean} true if the date >= today and the date has >= number of filter appointment number
     */
    public hasFullSchedule(day: number, forDisplay: boolean = true): boolean {
        let dateToCheck: Date = new Date(this.displayDate.getFullYear(), this.displayDate.getMonth(), day);
        let scheduleFull = false;
        if (Utils.CompareDates(this.minDate, dateToCheck) < 0) {
            return false;
        } else {
            if (this.schedule) {
                if (this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)] &&
                        this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)].totalRemainingAppointements === 0) {
                    this.schedule[Utils.getShortISOYearMonthDay(dateToCheck)].slots.map(s => {
                        if (s.active === 0 && s.total > 0) {
                            scheduleFull = true;
                        }
                    });
                }
            }
            return scheduleFull;
        }
    }

    /**
     * Creates the calendar display dates
     */
    private setCalendar() {
        this.displayDate = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), 1);
        this.weeks = new Array<any>();
        let dayOfWeek: number = this.displayDate.getDay(); // the day of the week the month starts on
        let daysInMonth: number; // the number of days in the display month
        let month: number = this.displayDate.getMonth() + 1; // the number of the month being displayed
        // set the number of days in the month
        switch (month) {
            case 1:
            case 3:
            case 5:
            case 7:
            case 8:
            case 10:
            case 12:
                daysInMonth = 31;
                break;
            case 4:
            case 6:
            case 9:
            case 11:
                daysInMonth = 30;
                break;
            case 2:
                if (Utils.isLeapYear(this.displayDate.getFullYear())) {
                    daysInMonth = 29;
                } else {
                    daysInMonth = 28;
                }
                break;
        }
        // build the calendar
        let day = 1; // day of the month we are setting
        let weekDay = 0; // week day 0 is sunday
        let week: Array<number> = new Array<number>(); // array of the days of the week
        let i = 0; // itterator for accessing the week array

        // create the first week of the month
        while (weekDay < 7) {
            // if the current week day we are setting is grater than or equal to the day of the week the month starts on
            // add the number to the week array
            if (weekDay >= dayOfWeek) {
                week[i] = day;
                day++; // increment the day
            } else {
                // if this day of the week is part of last month set it to null
                week[i] = null;
            }
            i++; // increment the itterator
            weekDay++; // add a day to the day of the week
        }
        // add the week to the weeks of this month
        this.weeks.push(week);

        // add the remaining days to the calendar
        while (daysInMonth >= day) {
            weekDay = 0;
            week = new Array<number>();
            i = 0;
            // create one week at a time
            while (weekDay < 7) {
                // if this day is part of next month set the day to null
                if (day > daysInMonth) {
                    week[i] = null;
                    i++;
                    weekDay++;
                } else {
                    week[i] = day;
                    day++;
                    i++;
                    weekDay++;
                }
            }
            // add the week to the weeks of this month
            this.weeks.push(week);
        }
        // set the display month
        this.month = 'GENERAL_REUSABLE.MONTHS.';
        switch (this.displayDate.getMonth() + 1) {
            case 1:
                this.month += 'JANUARY';
                break;
            case 2:
                this.month += 'FEBRUARY';
                break;
            case 3:
                this.month += 'MARCH';
                break;
            case 4:
                this.month += 'APRIL';
                break;
            case 5:
                this.month += 'MAY';
                break;
            case 6:
                this.month += 'JUNE';
                break;
            case 7:
                this.month += 'JULY';
                break;
            case 8:
                this.month += 'AUGUST';
                break;
            case 9:
                this.month += 'SEPTEMBER';
                break;
            case 10:
                this.month += 'OCTOBER';
                break;
            case 11:
                this.month += 'NOVEMBER';
                break;
            case 12:
                this.month += 'DECEMBER';
                break;
        }
        // set the display year
        this.year = this.displayDate.getFullYear().toString();
    }

    public getPosition(index: number): string {
        index++;
        if(index % 5 === 0 || index % 6 == 0 || index % 7 === 0) {
            return 'left';
        } else {
            return 'right';
        }
    }

}
